import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { saveAs } from 'file-saver';
import { User } from '../models/user.model';
import { Company } from '../models/company.model';
import { toast } from 'react-toastify';
import { EmployeeInvitationSendRequest } from '../models/employee.model';
import { TaskStatus } from '../models/task.status';

const apiClient = async (): Promise<AxiosInstance> => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if ('Clerk' in window && window.Clerk.session && window.Clerk.session.status === 'active') {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const token = await window.Clerk.session.getToken();
    return axios.create({
      baseURL: import.meta.env.VITE_API_URL.endsWith('/')
        ? import.meta.env.VITE_API_URL.substring(0, import.meta.env.VITE_API_URL.length - 1)
        : import.meta.env.VITE_API_URL,
      withCredentials: true,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }
  // No active Clerk session: fall back to a cookie-authenticated client
  // (withCredentials) so the declared Promise<AxiosInstance> always resolves.
  return axios.create({
    baseURL: import.meta.env.VITE_API_URL.endsWith('/')
      ? import.meta.env.VITE_API_URL.substring(0, import.meta.env.VITE_API_URL.length - 1)
      : import.meta.env.VITE_API_URL,
    withCredentials: true,
  });
};

// Add a request interceptor to include the csrftoken header
/*apiClient.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    // Handle request error
    return Promise.reject(error);
  }
);*/

export class NWClient {
  public static async currentUser(): Promise<User> {
    const axiosClient = await apiClient();
    return axiosClient
      .get('/api/user/current/')
      .then((response: AxiosResponse) => response.data?.[0]);
  }

  public static async getCurrentCompany(): Promise<Company[]> {
    const axiosClient = await apiClient();
    return new Promise((resolve, reject) => {
      axiosClient
        .get('/api/company/')
        .then((response: AxiosResponse) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  public static async sendInvitation(data: EmployeeInvitationSendRequest) {
    const axiosClient = await apiClient();
    return new Promise((resolve, reject) => {
      axiosClient
        .post(`api/employee-invitations/`, data)
        .then((response: AxiosResponse) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
          console.error(`Invitation could not be sent: ${error}`);
        });
    });
  }

  public static async DisclaimerCheck(): Promise<unknown> {
    const axiosClient = await apiClient();
    return new Promise((resolve, reject) => {
      axiosClient
        .get(`/api/user/disclaimercheck/`)
        .then((response: AxiosResponse<unknown>) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  public static async DisclaimerAccept(): Promise<unknown> {
    const axiosClient = await apiClient();
    return axiosClient
      .patch(`/api/user/disclaimercheck/`, null)
      .then((response: AxiosResponse<unknown>) => response.data)
      .catch((error) => {
        throw error;
      });
  }

  public static async subscriptionTermsCheck(): Promise<unknown> {
    const axiosClient = await apiClient();
    return new Promise((resolve, reject) => {
      axiosClient
        .get(`/api/user/subscriptiontermscheck/`)
        .then((response: AxiosResponse<unknown>) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  public static async subscriptionTermsAccept(): Promise<unknown> {
    const axiosClient = await apiClient();
    return axiosClient
      .patch(`/api/user/subscriptiontermscheck/`, null)
      .then((response: AxiosResponse<unknown>) => response.data)
      .catch((error) => {
        throw error;
      });
  }

  public static async downloadTemplate(id: number, filename: string): Promise<void> {
    const axiosClient = await apiClient();
    return new Promise((resolve, reject) => {
      axiosClient
        .get(`/api/template/${id}/download_file/`, {
          responseType: 'arraybuffer',
        })
        .then((response: AxiosResponse) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const blob = new Blob([response.data], { type: response.headers['content-type'] });
          saveAs(blob, filename);
          resolve();
        })
        .catch((error: AxiosError) => {
          reject(error);
        });
    });
  }

  public static async downloadDocument(id: number, filename: string): Promise<void> {
    const axiosClient = await apiClient();
    return new Promise((resolve, reject) => {
      axiosClient
        .get(`/api/download-document-upload/${id}`, {
          responseType: 'arraybuffer',
        })
        .then((response: AxiosResponse) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const blob = new Blob([response.data], { type: response.headers['content-type'] });
          saveAs(blob, filename);
          resolve();
        })
        .catch((error: AxiosError) => {
          reject(error);
        });
    });
  }

  public static async getDocumentBlob(id: number): Promise<string> {
    const axiosClient = await apiClient();
    return new Promise((resolve, reject) => {
      axiosClient
        .get(
          `${import.meta.env.VITE_API_URL}${import.meta.env.VITE_API_URL.endsWith('/') ? '' : '/'}api/download-document-upload/${id}`,
          {
            responseType: 'blob',
          }
        )
        .then((response: AxiosResponse) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const blob = new Blob([response.data], { type: response.headers['content-type'] });
          const fileURL = URL.createObjectURL(blob);
          resolve(fileURL);
        })
        .catch((error: AxiosError) => {
          reject(error);
        });
    });
  }

  public static subscribeToTaskStatus(
    taskIds: string[],
    onMessage: (data: TaskStatus) => void,
    onError?: (error: Event) => void
  ): () => void {
    if (taskIds.length === 0) {
      return () => {
        /* nothing to unsubscribe from */
      };
    }

    const eventSource = new EventSource(
      `${import.meta.env.VITE_API_URL}${import.meta.env.VITE_API_URL.endsWith('/') ? '' : '/'}api/task_status_stream?task_ids=${taskIds.join(
        '&task_ids='
      )}`
    );

    eventSource.onmessage = (event) => {
      try {
        const data: TaskStatus = JSON.parse(event.data);
        onMessage(data);
      } catch (error) {
        console.log(error);
      }
    };

    eventSource.onerror = (error) => {
      if (onError) {
        onError(error);
      }
      eventSource.close();
    };

    // Return cleanup function
    return () => {
      eventSource.close();
    };
  }

  public static async downloadHtmlDocument(
    id: number,
    filename: string,
    filetype?: string
  ): Promise<void> {
    const axiosClient = await apiClient();
    if (filetype == 'doc') {
      filetype = 'docx';
      filename = filename + 'x';
    }
    return new Promise((resolve, reject) => {
      axiosClient
        .get(`/api/document-upload-html-download/${id}?filetype=${filetype}`, {
          responseType: 'arraybuffer',
        })
        .then((response: AxiosResponse) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const blob = new Blob([response.data], { type: response.headers['content-type'] });
          saveAs(blob, filename);
          resolve();
        })
        .catch((error: AxiosError) => {
          reject(error);
        });
    });
  }

  public static async downloadLibraryHtmlDocument(
    id: number,
    fileType: string,
    filename?: string,
    ownTemplate?: boolean
  ): Promise<void> {
    const axiosClient = await apiClient();
    return new Promise((resolve, reject) => {
      axiosClient
        .get(
          `/api/document-${
            ownTemplate ? 'template' : 'library'
          }/${id}/download_file/?fileType=${fileType}`,
          {
            responseType: 'arraybuffer',
          }
        )
        .then((response: AxiosResponse) => {
          const contentDisposition = response.headers['content-disposition'];
          let fileName = filename ? filename : 'downloaded_file';
          if (contentDisposition) {
            const fileNameMatch = contentDisposition.match(/filename="([^"]*)"/);
            if (fileNameMatch && fileNameMatch.length === 1) {
              fileName = fileNameMatch[0];
            }
          } else {
            if (fileType === 'doc') {
              if (!fileName.endsWith('.docx')) {
                fileName += '.docx';
              }
            } else {
              if (!fileName.endsWith(`.${fileType}`)) {
                fileName += '.' + fileType;
              }
            }
          }
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const blob = new Blob([response.data], { type: response.headers['content-type'] });
          saveAs(blob, fileName);
          resolve();
          toast.success('File downloaded successfully');
        })
        .catch((error: AxiosError) => {
          reject(error);
        });
    });
  }

  public static async downloadLibraryDocument(id: number, filename: string): Promise<void> {
    const axiosClient = await apiClient();
    return new Promise((resolve, reject) => {
      axiosClient
        .get(`/api/document-library/${id}/download_file/`, {
          responseType: 'arraybuffer',
        })
        .then((response: AxiosResponse) => {
          const contentDisposition = response.headers['content-disposition'];
          let fileName = filename ? filename : 'downloaded_file';
          if (contentDisposition) {
            const fileNameMatch = contentDisposition.match(/filename="([^"]*)"/);
            if (fileNameMatch && fileNameMatch.length === 2) {
              fileName = fileNameMatch[1];
            }
          }
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const blob = new Blob([response.data], { type: response.headers['content-type'] });
          saveAs(blob, fileName);
          resolve();
          toast.success('File downloaded successfully');
        })
        .catch((error: AxiosError) => {
          reject(error);
        });
    });
  }

  public static async downloadDocumentContract(
    id: number,
    fileName: string,
    fileType: string
  ): Promise<void> {
    const axiosClient = await apiClient();
    if (fileType == 'doc') {
      fileType = 'docx';
      fileName = fileName + 'x';
    }
    return new Promise((resolve, reject) => {
      axiosClient
        .get(
          `/api/download-document-contract/${id}?doc_type=${fileType}${
            fileName ? `&file_name=${fileName}` : ''
          }`,
          {
            responseType: 'arraybuffer',
          }
        )
        .then((response: AxiosResponse) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const blob = new Blob([response.data], { type: response.headers['content-type'] });
          saveAs(blob, fileName);
          resolve();
        })
        .catch((error: AxiosError) => {
          reject(error);
        });
    });
  }

  public static async downloadTemplateInstance(id: number, filename: string): Promise<void> {
    const axiosClient = await apiClient();
    return new Promise((resolve, reject) => {
      axiosClient
        .get(`/api/template-instance/${id}/download_instance/`, {
          responseType: 'arraybuffer',
        })
        .then((response: AxiosResponse) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const blob = new Blob([response.data], { type: response.headers['content-type'] });
          saveAs(blob, filename);
          resolve();
        })
        .catch((error: AxiosError) => {
          reject(error);
        });
    });
  }

  public static async list<T = any>(entity: string, params?: unknown, data?: unknown): Promise<T> {
    const axiosClient = await apiClient();
    return new Promise((resolve, reject) => {
      axiosClient
        .get(`/api/${entity}/`, {
          params: params,
          data: data,
        })
        .then((response: AxiosResponse) => {
          resolve(response.data);
        })
        .catch((error: AxiosError) => {
          reject(error);
        });
    });
  }

  public static async get<T>(entity: string, id: number | string, params?: unknown): Promise<T> {
    const axiosClient = await apiClient();
    return new Promise((resolve, reject) => {
      axiosClient
        .get(`/api/${entity}/${id + ''}/`, {
          params: params,
        })
        .then((response: AxiosResponse) => {
          resolve(response.data);
        })
        .catch((error: AxiosError) => {
          reject(error);
        });
    });
  }

  public static async getByUser<T>(entity: string, user: number): Promise<T> {
    const axiosClient = await apiClient();
    return new Promise((resolve, reject) => {
      axiosClient
        .get(`/api/${entity}?user=${user}`)
        .then((response: AxiosResponse) => {
          resolve(response.data);
        })
        .catch((error: AxiosError) => {
          reject(error);
        });
    });
  }

  public static async post<T>(
    entity: string,
    data: Omit<T, 'id'>,
    contentTypeObject?: boolean,
    urlParam?: string
  ): Promise<T> {
    const axiosClient = await apiClient();
    return new Promise((resolve, reject) => {
      axiosClient
        .post(`/api/${entity}/${urlParam ? urlParam : ''}`, data, {
          headers: {
            'Content-Type': contentTypeObject ? 'multipart/form-data' : 'application/json',
          },
        })
        .then((response: AxiosResponse) => {
          resolve(response.data);
        })
        .catch((error: AxiosError) => {
          reject(error);
        });
    });
  }

  public static async put<T>(
    entity: string,
    id: number,
    data: Omit<T, 'id'>,
    closingSlash?: boolean,
    contentTypeObject?: boolean
  ): Promise<T> {
    const axiosClient = await apiClient();
    return new Promise((resolve, reject) => {
      axiosClient
        .put(`/api/${entity}/${id}${closingSlash ? '/' : ''}`, data, {
          headers: {
            'Content-Type': contentTypeObject ? 'multipart/form-data' : 'application/json',
          },
        })
        .then((response: AxiosResponse) => {
          resolve(response.data);
        })
        .catch((error: AxiosError) => {
          reject(error);
        });
    });
  }

  public static async patch<T>(
    entity: string,
    id: number | null,
    data: Partial<T>,
    closingSlash?: boolean
  ): Promise<T> {
    const axiosClient = await apiClient();
    return new Promise((resolve, reject) => {
      const url = id
        ? `/api/${entity}/${id}${closingSlash ? '/' : ''}`
        : `/api/${entity}${closingSlash ? '/' : ''}`;
      axiosClient
        .patch(url, data)
        .then((response: AxiosResponse) => {
          resolve(response.data);
        })
        .catch((error: AxiosError) => {
          reject(error);
        });
    });
  }

  public static async delete<T>(
    entity: string,
    id: number | string,
    params?: unknown,
    data?: unknown
  ): Promise<T> {
    const axiosClient = await apiClient();
    return new Promise((resolve, reject) => {
      axiosClient
        .delete(`/api/${entity}/${id + ''}/`, {
          params: params,
          data: data,
        })
        .then((response: AxiosResponse) => {
          resolve(response.data);
        })
        .catch((error: AxiosError) => {
          reject(error);
        });
    });
  }
}
